import React, { useState } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useForm } from 'react-hook-form';
import styled from 'styled-components';
import { FiServer, FiSave, FiRotateCcw, FiAlertCircle, FiCheckCircle } from 'react-icons/fi';

import WebsiteLayout from '../../../layout/website/WebsiteLayout';
import { createServer } from '../../../../store/servers/serversSlice';

const Page = styled.div`
  padding: 28px 24px;
  max-width: 760px;
  margin: 0 auto;
`;

const PageHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 28px;
  .icon {
    width: 44px; height: 44px; border-radius: 12px;
    background: rgba(147,51,234,0.12); border: 1px solid rgba(147,51,234,0.25);
    display: flex; align-items: center; justify-content: center;
    svg { color: #a78bfa; font-size: 20px; }
  }
  .title { font-size: 20px; font-weight: 700; color: #e2e8f0; }
  .sub { font-size: 12px; color: #6b7280; margin-top: 2px; }
`;

const Card = styled.div`
  background: #1A1A1A;
  border: 1px solid rgba(255,255,255,0.06);
  border-radius: 12px;
  padding: 28px 24px;
`;

const Row = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
  margin-bottom: 20px;
  @media (max-width: 560px) { grid-template-columns: 1fr; }
`;

const FormGroup = styled.div`
  margin-bottom: 20px;
  label {
    display: block; font-size: 12px; font-weight: 600; color: #9ca3af;
    margin-bottom: 6px; text-transform: uppercase; letter-spacing: 0.04em;
  }
  input, select, textarea {
    width: 100%; background: #111; border: 1px solid rgba(255,255,255,0.08);
    border-radius: 9px; padding: 10px 13px; color: #e2e8f0; font-size: 13.5px;
    font-family: inherit; outline: none; transition: border-color 0.2s;
    &::placeholder { color: #374151; }
    &:focus { border-color: rgba(147,51,234,0.5); }
  }
  textarea { resize: vertical; min-height: 80px; }
  select option { background: #1A1A1A; }
  .err { font-size: 12px; color: #f87171; margin-top: 5px; }
`;

const Divider = styled.div`
  height: 1px; background: rgba(255,255,255,0.06); margin: 24px 0;
`;

const BtnRow = styled.div`
  display: flex; gap: 10px; justify-content: flex-end; margin-top: 28px;
`;

const Btn = styled.button`
  display: flex; align-items: center; gap: 7px;
  padding: 10px 22px; border-radius: 9px; border: none;
  font-family: inherit; font-size: 13.5px; font-weight: 600;
  cursor: pointer; transition: opacity 0.15s;
  opacity: ${p => p.disabled ? 0.55 : 1};
  background: ${p => p.variant === 'reset' ? 'rgba(239,68,68,0.12)' : '#7C3AED'};
  color: ${p => p.variant === 'reset' ? '#f87171' : '#fff'};
  border: 1px solid ${p => p.variant === 'reset' ? 'rgba(239,68,68,0.3)' : 'transparent'};
  &:hover { opacity: 0.85; }
`;

const AlertBox = styled.div`
  display: flex; align-items: flex-start; gap: 10px;
  background: rgba(239,68,68,0.08); border: 1px solid rgba(239,68,68,0.25);
  border-radius: 9px; padding: 12px 16px; margin-bottom: 20px;
  svg { color: #f87171; font-size: 16px; flex-shrink: 0; margin-top: 1px; }
  .msg { font-size: 13px; color: #fca5a5; }
`;

const InfoBanner = styled.div`
  display: flex; align-items: flex-start; gap: 10px;
  background: rgba(251,191,36,0.07); border: 1px solid rgba(251,191,36,0.2);
  border-radius: 9px; padding: 12px 16px; margin-bottom: 24px;
  svg { color: #fbbf24; font-size: 16px; flex-shrink: 0; margin-top: 1px; }
  .msg { font-size: 13px; color: #fde68a; line-height: 1.5; }
`;

const SuccessBox = styled.div`
  display: flex; flex-direction: column; align-items: center; gap: 14px;
  padding: 48px 24px; text-align: center;
  svg { font-size: 52px; color: #34d399; }
  .title { font-size: 20px; font-weight: 700; color: #e2e8f0; }
  .sub { font-size: 14px; color: #6b7280; max-width: 360px; }
`;

const AddServer = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const auth = useSelector(state => state.auth);
  const servers = useSelector(state => state.servers);
  const [submitted, setSubmitted] = useState(false);

  const { register, handleSubmit, reset, formState: { errors } } = useForm({
    defaultValues: { type: 'newschool', server_language: 'arabic', level: 99, wars: 50, farms: 50 }
  });

  if (!auth.isAuth) return <Navigate to="/login" />;

  const onSubmit = (values) => {
    const fd = new FormData();
    Object.entries(values).forEach(([k, v]) => {
      if (k === 'image' && v?.[0]) fd.append('image', v[0]);
      else if (v !== undefined && v !== null && v !== '') fd.append(k, v);
    });
    dispatch(createServer(fd)).unwrap().then(() => {
      setSubmitted(true);
    });
  };

  return (
    <WebsiteLayout>
      <Page>
        <PageHeader>
          <div className="icon"><FiServer /></div>
          <div>
            <div className="title">{t('website.add_server.title', 'إضافة سيرفر')}</div>
            <div className="sub">{t('website.add_server.sub', 'أضف سيرفرك وانتظر موافقة الإدارة')}</div>
          </div>
        </PageHeader>

        <Card>
          {submitted ? (
            <SuccessBox>
              <FiCheckCircle />
              <div className="title">{t('website.add_server.success_title', 'تم إرسال طلبك بنجاح!')}</div>
              <div className="sub">
                {t('website.add_server.success_sub', 'سيرفرك قيد المراجعة، ستتم إضافته بعد موافقة الإدارة.')}
              </div>
              <Btn onClick={() => navigate('/profile')} style={{ marginTop: 8 }}>
                {t('website.add_server.go_profile', 'عرض سيرفراتي')}
              </Btn>
            </SuccessBox>
          ) : (
            <>
              <InfoBanner>
                <FiAlertCircle />
                <div className="msg">
                  {t('website.add_server.pending_note', 'سيرفرك سيظهر في القائمة بعد مراجعة الإدارة والموافقة عليه.')}
                </div>
              </InfoBanner>

              {servers.errors.length > 0 && (
                <AlertBox>
                  <FiAlertCircle />
                  <div className="msg">{servers.errors.map((e, i) => <div key={i}>{e?.msg}</div>)}</div>
                </AlertBox>
              )}

              <form onSubmit={handleSubmit(onSubmit)}>
                <Row>
                  <FormGroup>
                    <label>{t('website.add_server.name_ar', 'الاسم بالعربي')}</label>
                    <input placeholder="اسم السيرفر" {...register('name_ar', { required: t('validation.required', 'مطلوب') })} />
                    {errors.name_ar && <div className="err">{errors.name_ar.message}</div>}
                  </FormGroup>
                  <FormGroup>
                    <label>{t('website.add_server.name_en', 'Server name (English)')}</label>
                    <input placeholder="Server name" {...register('name_en')} />
                  </FormGroup>
                </Row>

                <Row>
                  <FormGroup>
                    <label>{t('website.add_server.desc_ar', 'الوصف بالعربي')}</label>
                    <textarea placeholder="وصف السيرفر..." {...register('description_ar')} />
                  </FormGroup>
                  <FormGroup>
                    <label>{t('website.add_server.desc_en', 'Description (English)')}</label>
                    <textarea placeholder="Server description..." {...register('description_en')} />
                  </FormGroup>
                </Row>

                <Divider />

                <Row>
                  <FormGroup>
                    <label>{t('website.add_server.type', 'النوع')}</label>
                    <select {...register('type')}>
                      <option value="newschool">New School</option>
                      <option value="oldschool">Old School</option>
                      <option value="middleschool">Middle School</option>
                      <option value="custom">Custom</option>
                    </select>
                  </FormGroup>
                  <FormGroup>
                    <label>{t('website.add_server.language', 'لغة السيرفر')}</label>
                    <select {...register('server_language')}>
                      <option value="arabic">عربي</option>
                      <option value="foreign">أجنبي</option>
                    </select>
                  </FormGroup>
                </Row>

                <Row>
                  <FormGroup>
                    <label>{t('website.add_server.level', 'أقصى مستوى')}</label>
                    <input type="number" min={1} {...register('level')} />
                  </FormGroup>
                </Row>

                <Row>
                  <FormGroup>
                    <label>{t('website.add_server.wars', 'نسبة الحروب %')}</label>
                    <input type="number" min={0} max={100} {...register('wars')} />
                  </FormGroup>
                  <FormGroup>
                    <label>{t('website.add_server.farms', 'نسبة الزراعة %')}</label>
                    <input type="number" min={0} max={100} {...register('farms')} />
                  </FormGroup>
                </Row>

                <Divider />

                <Row>
                  <FormGroup>
                    <label>{t('website.add_server.website_url', 'رابط الموقع')}</label>
                    <input type="url" placeholder="https://..." {...register('website_url')} />
                  </FormGroup>
                  <FormGroup>
                    <label>{t('website.add_server.discord_url', 'رابط ديسكورد')}</label>
                    <input type="url" placeholder="https://discord.gg/..." {...register('discord_url')} />
                  </FormGroup>
                </Row>

                <FormGroup>
                  <label>{t('website.add_server.image', 'صورة السيرفر')}</label>
                  <input type="file" accept="image/*" {...register('image')} style={{ padding: '6px' }} />
                </FormGroup>

                <BtnRow>
                  <Btn type="button" variant="reset" onClick={() => reset()} disabled={servers.isLoading}>
                    <FiRotateCcw /> {t('website.add_server.reset', 'مسح')}
                  </Btn>
                  <Btn type="submit" disabled={servers.isLoading}>
                    <FiSave /> {servers.isLoading ? t('website.add_server.saving', 'جاري الإرسال...') : t('website.add_server.submit', 'إرسال الطلب')}
                  </Btn>
                </BtnRow>
              </form>
            </>
          )}
        </Card>
      </Page>
    </WebsiteLayout>
  );
};

export default AddServer;
